/*
 * The island's own key bindings for the two things it cannot resolve locally:
 * leaving the island by arrow key, and undo/redo once its local history is
 * spent (design §2.a/2.b).
 *
 * WHY THESE ARE FUNCTION CALLS, NOT KEY EVENTS. The suggestion widget is
 * created with `stopEvent: () => true` (AIProposalsExtension), and ProseMirror
 * checks that predicate ahead of BOTH the host's key handling and its
 * `handleDOMEvents` — so no keystroke made inside an island can reach the host
 * view, by design, and weakening that would let every island keystroke
 * double-dispatch into the note. The handlers here therefore reach the host as
 * plain calls over the IslandActions bridge; the containment stays absolute.
 *
 * UNDO IS A HANDOFF, NOT A SHARED HISTORY. Cmd+Z first exhausts the island's
 * own history. Once that is empty the island FLUSHES its draft, CLOSES, and the
 * press runs the host note's undo chain with the host focused — so from the
 * second press on there is exactly one live history rather than two branching
 * ones. That matters clinically: an accept's content lives in the host document
 * and its undo flips the record back to pending with FHIR compensation, while
 * island edits never enter the shared doc at all. Leaving both histories live
 * and interleaving them lets a later redo replay one surface against a document
 * the other has since branched. Handing off keeps every press after the
 * boundary on the surface that owns what it is undoing.
 *
 * The undo/redo handlers always report the key as handled, even when both the
 * island and the host have nothing left. Returning false there would leave the
 * keydown un-defaulted inside a contenteditable, where Chrome's native undo
 * becomes the handler of last resort against a ProseMirror-managed DOM.
 */
import { type AnyExtension, Extension } from '@tiptap/core'
import type { EditorState } from '@tiptap/pm/state'
import { Selection } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import type { IslandExitSide } from '#src/components/rich-text-editor/islandTraversal.ts'

export interface IslandKeymapHandlers {
  // Run the host note's undo chain. Returns whether anything was undone.
  onUndoBeyondHistory: () => boolean
  onRedoBeyondHistory: () => boolean
  // Move the caret out of the island to `side` of its card.
  onExit: (side: IslandExitSide) => boolean
}

export function isAtIslandDocStart(state: EditorState): boolean {
  const { selection } = state
  return selection.empty && selection.from <= Selection.atStart(state.doc).from
}

export function isAtIslandDocEnd(state: EditorState): boolean {
  const { selection } = state
  return selection.empty && selection.from >= Selection.atEnd(state.doc).from
}

/*
 * Visual-line edges, for the VERTICAL arrows only.
 *
 * The document-position tests above answer "is the caret at the island's first
 * or last position". That is the right question for Left/Right, and it is the
 * same question for Up/Down only while the island is one line tall. The moment
 * a clinician's edit wraps or adds a break the two diverge: with the caret at
 * the START of the last line there is no line below it, but it is nowhere near
 * the document end -- so a position-only test refused to exit, ArrowDown fell
 * through to the browser, and with nothing below it the caret was put back at
 * the island's top. Down became a cycle while Right, which walks to the true
 * document end first, exited fine.
 *
 * `endOfTextblock` asks the BROWSER whether a vertical move would leave the
 * current textblock, so it counts a soft wrap and a <br> alike -- neither of
 * which carries a document position for `Selection.atEnd` to find. The
 * position test is kept as the first arm: it is what holds in environments
 * with no layout to measure (jsdom), where `endOfTextblock` cannot answer.
 */
export function isAtIslandVisualStart(view: EditorView): boolean {
  const { state } = view
  const { selection } = state
  if (!selection.empty) {
    return false
  }
  if (isAtIslandDocStart(state)) {
    return true
  }
  const { $from } = selection
  const blockStart = $from.depth === 0 ? 0 : $from.before($from.depth)
  return blockStart <= 0 && view.endOfTextblock('up')
}

export function isAtIslandVisualEnd(view: EditorView): boolean {
  const { state } = view
  const { selection, doc } = state
  if (!selection.empty) {
    return false
  }
  if (isAtIslandDocEnd(state)) {
    return true
  }
  const { $from } = selection
  const blockEnd =
    $from.depth === 0 ? doc.content.size : $from.after($from.depth)
  return blockEnd >= doc.content.size && view.endOfTextblock('down')
}

/*
 * Priority 200 so these bindings deterministically beat StarterKit's UndoRedo
 * and the gap-cursor arrow handling, both at Tiptap's default 100 (Tiptap
 * registers keymap plugins in descending priority and the first handler to
 * claim a key wins — the same reasoning as AIProposalUndoExtension's 1001).
 * Without it StarterKit's own Mod-z would run first and swallow the press on an
 * exhausted history.
 *
 * Handlers are read through the caller's object on every press rather than
 * captured, so one extension instance survives the island's whole life while
 * still seeing current props.
 */
export function createIslandKeymap(
  handlers: IslandKeymapHandlers
): AnyExtension {
  return Extension.create({
    name: 'islandKeymap',
    priority: 200,
    addKeyboardShortcuts() {
      const history = (direction: 'undo' | 'redo') => (): boolean => {
        const ran =
          direction === 'undo'
            ? (this.editor.commands.undo?.() ?? false)
            : (this.editor.commands.redo?.() ?? false)
        if (!ran) {
          if (direction === 'undo') {
            handlers.onUndoBeyondHistory()
          } else {
            handlers.onRedoBeyondHistory()
          }
        }
        return true
      }
      // Both arrows on a side mean the same move: the card is a `block` box,
      // so the text beside the island and the line beyond it are the same
      // place. A shift- or mod-modified arrow is a different prosemirror
      // keymap name and never reaches these, so extending a selection to the
      // island's edge cannot fling the caret out of it.
      const exitHorizontal = (side: IslandExitSide) => (): boolean => {
        const atEdge =
          side === 'before'
            ? isAtIslandDocStart(this.editor.state)
            : isAtIslandDocEnd(this.editor.state)
        return atEdge && handlers.onExit(side)
      }
      const exitVertical = (side: IslandExitSide) => (): boolean => {
        const atEdge =
          side === 'before'
            ? isAtIslandVisualStart(this.editor.view)
            : isAtIslandVisualEnd(this.editor.view)
        return atEdge && handlers.onExit(side)
      }
      return {
        'Mod-z': history('undo'),
        'Mod-Shift-z': history('redo'),
        'Mod-y': history('redo'),
        ArrowUp: exitVertical('before'),
        ArrowLeft: exitHorizontal('before'),
        ArrowDown: exitVertical('after'),
        ArrowRight: exitHorizontal('after'),
      }
    },
  })
}
